<template>
  <div class="v-centerer f-w f-h login">
    <img class="login__logo" alt="" :src="require('@/assets/img/logo-full.png')" />
    <div class="login__tabs-wrapper">
      <h1 class="basic-blue-title">{{ $t('message.LOGIN') }}</h1>

      <Tabs v-model="selectedLoginOption" :tabs="loginOptions">

        <div class="login__tabs-content-wrapper">
          <div v-if="!$store.state.attemptingLogin" class="login__tabs-content">

            <form v-if="[0, 1].includes(selectedLoginOption)" class="login__form" @submit.prevent="login()">
              <label for="id" class="legend legend--bottom-margin login__form__centered">
                {{ formProps.idLabel }}
              </label>
              <input
                id="id"
                v-model="formProps.params.id"
                class="login__form__centered generic-border--thin-border generic-border--focus-shadow"
                type="text"
                :placeholder="formProps.idPlaceholder"
                autocomplete="on"
              >
              <label for="pwd" class="legend legend--bottom-margin login__form__centered">
                {{ $t("message.LOGIN_PWD") }}
              </label>
              <input
                id="pwd"
                v-model="formProps.params.password"
                class="login__form__centered generic-border--thin-border generic-border--focus-shadow"
                type="password"
                :placeholder="$t('message.LOGIN_PWD')"
                autocomplete="current-password"
              >
              <Checkbox
                v-model="formProps.params.keepAlive"
                :title="$t('message.STAY_CONNECTED')"
                style="padding: 0px; margin-bottom: 20px;"
              />
              <br>
              <div v-if="warningMessage" class="login__form__warning">
                {{ warningMessage }}
              </div>
              <button class="generic-border generic-border--text-btn generic-border--blue">
                {{ $t('message.LOGIN_SUBMIT') }}
              </button>
              <div class="option-link mt10 mb10">
                <span @click="forgotPass">{{ $t("message.FORGOT_PASSWORD") }}</span>
              </div>
            </form>

            <form v-else class="login__form" @submit.prevent="login()">
              <div class="field-hint login__form__centered">
                {{ $t('message.LOGIN_BY_SSO_HINT') }}
              </div>
              <Switcher v-model="selectedSsoLoginOption" :titles="ssoLoginOptions" />
              <br>
              <label for="idSso" class="legend legend--bottom-margin login__form__centered">
                {{ formProps.idLabel }}
              </label>
              <input
                id="idSso"
                v-model="formProps.params.id"
                class="login__form__centered generic-border--thin-border generic-border--focus-shadow"
                type="text"
                :placeholder="formProps.idPlaceholder"
                autocomplete="on"
              >
              <button class="generic-border generic-border--text-btn generic-border--blue">
                {{ $t('message.LOGIN_SUBMIT') }}
              </button>
            </form>
          </div>
        </div>
      </Tabs>

      <LoadingOverlay :value="$store.state.attemptingLogin" text="">
        <div class="v-centerer">
          <Icon>{{ lockMedium }}</Icon>
          <span class="ml10em">{{ $t('message.SECURE_AUTHENTICATION') }}</span>
        </div>
      </LoadingOverlay>
    </div>
  </div>
</template>

<script>
/**
 * Login page component.
 * Prompts the user with 3 login options:
 * 1. Login as a team to have access to this team and all the supervised teams.
 * 2. Login as a user. Same as the above. Additionnally, a user may be a member of multiple teams.
 *    If so, he will be able to switch between those teams without logging out.
 * 3. Login as a user with SSO. Same as the above. Although the user will not need a password as he
 *    will be redirected to the Identity Provider of his company.
 */

import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex utils
import { LOGIN } from '@/store/action-types';
// Components
import Checkbox from '@/components/checkbox.vue';
import Icon from '@/components/icon.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import Switcher from '@/components/switcher.vue';
import Tabs from '@/components/tabs.vue';
// Utils
import { calert, handleUserEditionError } from '@/utils/main';
import { LSK_LOGIN_MODE, LSK_SSO_LOGIN_MODE, LSK_SSO_LOGIN_USER, LSK_SSO_LOGIN_TEAM } from '@/utils/constants';
import ErrorCode from '@/utils/classes/ErrorCode';
import { lockMedium } from '@/assets/icons';

export default {

  name: 'Login',

  components: {
    Checkbox,
    Icon,
    LoadingOverlay,
    Switcher,
    Tabs,
  },

  data() {
    return {
      lockMedium,
      teamParams: {
        id: '',
        password: '',
        keepAlive: true,
        lsk: LSK_SSO_LOGIN_TEAM,
      },
      userParams: {
        id: '',
        password: '',
        keepAlive: true,
        user: true,
        lsk: LSK_SSO_LOGIN_USER,
      },
      ssoLoginOptions: [
        this.$t('message.TEAM'),
        this.$t('message.USER'),
      ],
      warningMessage: null,
    };
  },

  computed: {
    formProps() {
      if (this.selectedLoginOption === 0 || (this.selectedLoginOption === 2 && this.selectedSsoLoginOption === 0)) {
        return {
          idLabel: this.$t('message.LOGIN_TEAM_ID'),
          idPlaceholder: this.$t('message.LOGIN_TEAM_ID_EXAMPLE'),
          params: this.teamParams,
        };
      }
      return {
        idLabel: this.$t('message.LOGIN_USER_ID'),
        idPlaceholder: this.$t('message.LOGIN_USER_ID_EXAMPLE'),
        params: this.userParams,
      };
    },
    /** Array: Available login option tabs */
    loginOptions() {
      if (this.$store.state.device.isSmartphonePortrait) {
        return [
          this.$t('message.LOGIN_BY_TEAM_SHORT'),
          this.$t('message.LOGIN_BY_USER_SHORT'),
          this.$t('message.LOGIN_BY_SSO_SHORT'),
        ];
      }
      return [
        this.$t('message.LOGIN_BY_TEAM'),
        this.$t('message.LOGIN_BY_USER'),
        this.$t('message.LOGIN_BY_SSO'),
      ];
    },
    /** Number: Index of the currently focused view (login by team/user/SSO) */
    selectedLoginOption: {
      get() {
        const defaultLoginMode = window.localStorage.getItem(LSK_LOGIN_MODE) || 0;
        return Number(this.$route.query.loginSelectedOption || defaultLoginMode) % this.loginOptions.length;
      },
      set(view) {
        const query = Object.assign({}, this.$route.query);
        query.loginSelectedOption = Number(view || 0) % this.loginOptions.length;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: Index of the selected option when logging in using SSO */
    selectedSsoLoginOption: {
      get() {
        const defaultLoginMode = window.localStorage.getItem(LSK_SSO_LOGIN_MODE) || 0;
        return Number(this.$route.query.ssoLoginSelectedOption || defaultLoginMode) % this.ssoLoginOptions.length;
      },
      set(view) {
        const query = Object.assign({}, this.$route.query);
        query.ssoLoginSelectedOption = Number(view || 0) % this.ssoLoginOptions.length;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    keepAlive(newValue) {
      localStorage.setItem('loginKeepAlive', newValue);
    },
    selectedLoginOption() {
      this.setLocalStorageValue();
    },
    selectedSsoLoginOption() {
      this.setLocalStorageValue();
    },
    'teamParams.id'() {
      this.warningMessage = null;
    },
    'userParams.id'() {
      this.warningMessage = null;
    },
  },

  created() {
    const loginKeepAlive = localStorage.getItem('loginKeepAlive');
    if (loginKeepAlive === null) {
      localStorage.setItem('loginKeepAlive', this.keepAlive);
    } else {
      this.keepAlive = (loginKeepAlive === 'true');
    }

    this.$nextTick(this.setLocalStorageValue);
  },

  mounted() {
    // Check potential SSO login errors
    const query = Object.assign({}, this.$route.query);
    let { ssoLoginError } = query;
    if (ssoLoginError !== undefined) {
      ssoLoginError = Number(ssoLoginError);
      let errMsg;
      if (ssoLoginError === ErrorCode.LOGIN_SSO_WRONG_PARAMS) {
        // Wrong SSO params in cookies (User may have changed the cookies himself)
        errMsg = this.$t('message.CONNECTION_ERROR');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_NOT_ENABLED) {
        // User tried to connect via SSO but SSO isn't configured for the company
        errMsg = this.$t('message.LOGIN_SSO_NOT_ENABLED');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_IDP_FAILED) {
        // IdP didn't authenticate the user
        errMsg = this.$t('message.LOGIN_SSO_IDP_FAILED_AUTHENTICATION');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_WRONG_MAPPER_IDP_PROP) {
        // Mapper IdP property wasn't found in result from IdP
        errMsg = this.$t('message.LOGIN_SSO_MAPPER_WRONG_IDP_PROP');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_MAPPER_USER_NOT_FOUND) {
        // User wasn't found when trying to match IdP result
        errMsg = this.$t('message.LOGIN_SSO_USER_NOT_FOUND');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_NEW_USER_PROPS_MISSING) {
        // User wasn't found and some props were missing when trying to create his account
        // Either givenName, surname or groups
        errMsg = this.$t('message.LOGIN_SSO_NEW_USER_PROPS_MISSING');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_NEW_USER_TEAM_NOT_FOUND) {
        // When trying to match the 'groups' parameter from IdP, no Shizen team matches
        errMsg = this.$t('message.LOGIN_SSO_NEW_USER_NO_TEAM_MATCH');
      } else if (ssoLoginError === ErrorCode.LOGIN_SSO_NEW_USER_CREATION_ERROR) {
        // Error when trying to create user account
        errMsg = this.$t('message.LOGIN_SSO_NEW_USER_CREATION_ERROR');
        if (query.ssoLoginErrorData) {
          const errorData = JSON.parse(query.ssoLoginErrorData);
          if (errorData && errorData.code && errorData.userObject) {
            const msg = handleUserEditionError({ data: errorData }, false);
            errMsg = `${errMsg}${this.$t('message.TWO_DOTS')}${msg}`;
          }
          delete query.ssoLoginErrorData;
        }
      }
      if (errMsg) {
        if (ssoLoginError !== ErrorCode.LOGIN_SSO_WRONG_PARAMS) {
          errMsg = `${errMsg}<br>${this.$t('message.CONTACT_SHIZEN_ADMIN')}`;
        }
        toastr.error(errMsg, '', { timeOut: 30000, extendedTimeOut : 30000, progressBar: true });
      }
      delete query.ssoLoginError;
      this.$router.replace({ query });
    }
  },

  methods: {
    /** On SSO mode, if user hasn't tped anything yet, set default values from localstorage */
    setLocalStorageValue() {
      if (this.selectedLoginOption === 2 && this.formProps.params.id === '') {
        const val = window.localStorage.getItem(this.formProps.params.lsk);
        if (val !== null) {
          this.formProps.params.id = val;
        }
      }
    },
    login() {
      // Save login mode to localstorage
      window.localStorage.setItem(LSK_LOGIN_MODE, this.selectedLoginOption);
      if (this.selectedLoginOption === 2) {
        window.localStorage.setItem(LSK_SSO_LOGIN_MODE, this.selectedSsoLoginOption);
        window.localStorage.setItem(this.formProps.params.lsk, this.formProps.params.id);
      }
      // Try logging in
      this.$store.dispatch(LOGIN, { ...this.formProps.params, sso: this.selectedLoginOption === 2 ? 1 : undefined })
        .catch((error) => {
          if (error.response && error.response.data) {
            // Add a warning message below the field if the user is likely to lock his account
            const { response: { data } } = error;
            if (data && data instanceof Object && data.code !== undefined) {
              if (data.lockOnNextFail) {
                this.warningMessage = this.$t('message.LOGIN_LOCK_ON_NEXT_FAIL');
              } else if (data.lockedAfterFailedAttempts) {
                this.warningMessage = this.$t('message.LOGIN_LOCKED_AFTER_FAILED_LOGIN');
              } else {
                this.warningMessage = null;
              }
            }
          }
        });
    },
    /** Prompt the user to receive a new password */
    forgotPass(evt, msg, val) {
      msg = msg || (this.$t(this.selectedLoginOption === 1 ? 'message.NEED_NEW_PWD_USER' : 'message.NEED_NEW_PWD'));
      const ph = this.$t(this.selectedLoginOption === 1 ? 'message.NEW_PWD_USER_ID' : 'message.NEW_PWD_TEAM_ID');
      calert(
        msg,
        [
          { title: this.$t('message.YES'), action: this.createNewPassword },
          { title: this.$t('message.CANCEL') },
        ],
        false,
        '',
        [{ value: val || '', ph }],
      );
    },
    /** Reset the password of the team */
    createNewPassword(id) {
      const [tid] = id;
      this.$http({
        method: 'post',
        url: this.selectedLoginOption === 0 ? '/server/teams/resetPwd.php' : '/server/users/resetPwd.php',
        data: { id: tid, locale: this.$t('min') },
      })
        .then(({ data }) => {
          if (data === -2) {
            this.forgotPass(null, this.$t('message.NEW_PWD_WRONG_FORMAT'), id);
          } else if (data === -1) {
            this.forgotPass(null, this.$t('message.NEW_PWD_NO_MATCH'), id);
          } else if (data === 1) {
            toastr.success(this.$t('message.PWD_RESET_SUCCESS'));
          }
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.login, .config-login {
  position: relative;

  &__logo {
    max-height: 45px;
    position: absolute;
    top: 1rem;
    left: 1rem;
  }

  // Fix display of the logo in the navbar
  .nav-bar {
    padding: 3px;

    &__logo {
      height: 2.2rem;
      margin-left: 0.4rem;
      margin-top: 0.4rem;

      img {
        height: 100%;
      }
    }
  }

  #page-content {
    top: 0px;
    height: 100%;
    margin-left: $smartphone-nav-width;
    width: calc(100% - #{$smartphone-nav-width});
  }

  &__tabs-wrapper {
    width: 50%;
    margin: auto;
    text-align: center;
    z-index: 10006;
    background: #ffffff;
    border-radius: $main-border-radius;
    box-shadow: $light-box-shadow;
    overflow: hidden;
    padding: 1rem;

    .tabs {
      --tabscolor: #{$color-shizen-blue};
      --tabs-hover-color: #{$color-shizen-blue-light};
      --tabs-focused-text-color: #fff;
    }
  }

  h1.basic-blue-title {
    background: transparent;
    padding: 1.2rem 0px;
    line-height: 1.9rem;
    margin-bottom: 1rem;
  }

  &__tabs-content-wrapper {
    margin: 1rem;
    border-radius: $main-border-radius;
    background: #fff;
    min-height: 400px;
  }

  &__tabs-content {

    .generic-border--text-btn {
      margin: auto;
    }
  }

  &__form {
    flex: 1;
    overflow: auto;
    padding: 3em 0px;

    .switcher {
      margin: 1em 0px;
    }

    .action-btn.save-btn {
      position: relative;
      bottom: unset;
      display: block;
    }

    &__centered {
      display: block;
      width: 70%;
      margin: 1rem auto 2rem auto;
      border: none;
      padding: 3px;
      font-weight: 400;
      color: #58595B;
      background: transparent;
      text-align: left;
      // Outline is done using generic-border--focus-shadow
      outline: none;

      &:first-child {
        margin-top: 0px;
      }

      &::placeholder {
        font-weight: 400;
        color: #98999B;
      }
    }

    &__warning {
      color: $color-red-bad;
      padding: 1em 2em;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .login, .config-login {
    &__tabs-wrapper {
      width: 90%;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .login, .config-login {
    &__tabs-wrapper {
      width: 100%;
    }
  }
}
</style>
